// ============================================================================
// UNIVERSAL PARALLAX FOR .par-animate ELEMENTS
// ============================================================================

class ParallaxAnimator {
  constructor() {
    // Find all elements with the 'par-animate' class that should have parallax effects
    this.parallaxElements = document.querySelectorAll('.par-animate')
    this.init()
  }

  init() {
    this.setupParallaxEffects()
    this.setupEventListeners()
    this.handleParallax() // Run on load to set initial positions
  }

  setupParallaxEffects() {
    // Process each parallax element and set up their speed values
    this.parallaxElements.forEach((element) => {
      // Get the parallax effect type from the data attribute (e.g., 'vertical_fast', 'horizontal_slow')
      const effect = element.dataset.parallaxEffect
      // Calculate the speed multipliers for X and Y movement
      const speeds = this.getParallaxSpeeds(effect)

      // Store the speed values as data attributes for use in the parallax calculation
      element.dataset.parallaxSpeed = speeds.y  // Vertical speed (Y-axis)
      element.dataset.xSpeed = speeds.x         // Horizontal speed (X-axis)
    })
  }

  getParallaxSpeeds(effect) {
    // Define speed multipliers for different parallax effects
    // Positive values move in the direction of scroll, negative values move opposite
    const speeds = {
      y: 0,  // Vertical speed (0 = no vertical movement)
      x: 0   // Horizontal speed (0 = no horizontal movement)
    }

    switch (effect) {
      case 'vertical_fast':
        speeds.y = 0.6  // Fast downward movement (moves down as you scroll down)
        break
      case 'vertical_slow':
        speeds.y = 0.08 // Slow downward movement
        break
      case 'vertical_fast_reverse':
        speeds.y = -0.6 // Fast upward movement (moves up as you scroll down)
        break
      case 'vertical_slow_reverse':
        speeds.y = -0.08 // Slow upward movement
        break
      case 'horizontal_fast':
        speeds.x = 0.8  // Fast rightward movement
        break
      case 'horizontal_slow':
        speeds.x = 0.08 // Slow rightward movement
        break
      case 'horizontal_fast_reverse':
        speeds.x = -0.8 // Fast leftward movement
        break
      case 'horizontal_slow_reverse':
        speeds.x = -0.08 // Slow leftward movement
        break
    }

    return speeds
  }

  setupEventListeners() {
    // Listen for scroll events to update parallax positions
    window.addEventListener('scroll', () => this.handleParallax())
    // Listen for resize events to recalculate positions when window size changes
    window.addEventListener('resize', () => this.handleParallax())
  }

  handleParallax() {
    this.parallaxElements.forEach((element) => {
      // Get the bounding rectangle of the parent element (usually the slide container)
      const parentRect = element.parentElement.getBoundingClientRect()

      // Check if the parent element is visible in the viewport
      // This prevents unnecessary calculations for off-screen elements
      const isVisible = parentRect.top < window.innerHeight && parentRect.bottom > 0

      if (isVisible) {
        // Get the speed values we set up earlier
        const speed = parseFloat(element.dataset.parallaxSpeed) || 0  // Vertical speed
        const xSpeed = parseFloat(element.dataset.xSpeed) || 0        // Horizontal speed

        // ============================================================================
        // THE JUMPING BEHAVIOR EXPLANATION:
        // ============================================================================
        // This calculation determines how far through the scroll the element should be
        // It creates a "jump" effect because it's based on the element's position relative to viewport

        // Calculate scroll progress (0 to 1) based on element's position in viewport
        // When element first enters viewport: parentRect.top = window.innerHeight (progress = 0)
        // When element is centered: parentRect.top = window.innerHeight/2 (progress = 0.5)
        // When element exits viewport: parentRect.top = -parentRect.height (progress = 1)
        const scrollProgress = Math.max(0, Math.min(1, (window.innerHeight - parentRect.top) / (window.innerHeight + parentRect.height)))

        // Apply easing function to make movement more natural (ease-out)
        // This creates a smooth deceleration effect
        const easedProgress = 1 - Math.pow(1 - scrollProgress, 2)

        // ============================================================================
        // VERTICAL POSITION CALCULATION (Y-axis):
        // ============================================================================
        // This is where the "jumping" happens:
        // - parentRect.top * speed: Creates the parallax effect
        // - easedProgress: Smooths the movement
        // - Math.min(70, ...): Limits maximum movement to 70px to prevent excessive jumping
        // - Negative sign: Inverts the direction so positive speed moves element down
        const yPos = Math.min(70, -(parentRect.top * speed * easedProgress))

        // ============================================================================
        // HORIZONTAL POSITION CALCULATION (X-axis):
        // ============================================================================
        // Uses window.scrollY (total scroll distance) instead of element position
        // This creates a continuous horizontal movement regardless of element visibility
        const xPos = window.scrollY * xSpeed * easedProgress

        // Apply the calculated positions using CSS transform
        // translate3d forces hardware acceleration for smoother performance
        element.style.transform = `translate3d(${xPos}px, ${yPos}px, 0)`
      }
    })
  }
}

// ============================================================================
// SIMPLE PARALLAX FOR .par-animate-simple ELEMENTS (CONTAINER-AWARE)
// ============================================================================

class ParallaxSimpleAnimator {
  constructor() {
    this.simpleElements = document.querySelectorAll('.par-animate-simple')
    this.elementContexts = []
    this.init()
  }

  init() {
    this.setupSimpleEffects()
    this.setupEventListeners()
    this.handleSimpleParallax()
  }

  setupSimpleEffects() {
    this.elementContexts = []
    this.simpleElements.forEach((element) => {
      const effect = element.dataset.parallaxEffect
      const speeds = this.getSimpleParallaxSpeeds(effect)
      element.dataset.simpleSpeed = speeds.y
      element.dataset.simpleXSpeed = speeds.x
      if (!element.dataset.initialY) {
        element.dataset.initialY = '0'
        element.dataset.initialX = '0'
      }
      // Find the closest .shopify-section ancestor
      let section = element.closest('.shopify-section')
      if (!section) section = window
      this.elementContexts.push({ element, section })
    })
  }

  getSimpleParallaxSpeeds(effect) {
    const speeds = { y: 0, x: 0 }
    switch (effect) {
      case 'vertical_fast': speeds.y = 0.15; break
      case 'vertical_slow': speeds.y = 0.05; break
      case 'vertical_fast_reverse': speeds.y = -0.15; break
      case 'vertical_slow_reverse': speeds.y = -0.05; break
      case 'horizontal_fast': speeds.x = 0.2; break
      case 'horizontal_slow': speeds.x = 0.05; break
      case 'horizontal_fast_reverse': speeds.x = -0.2; break
      case 'horizontal_slow_reverse': speeds.x = -0.05; break
    }
    return speeds
  }

  setupEventListeners() {
    if (this._listeners) {
      this._listeners.forEach(({ section, handler }) => {
        if (section === window) {
          window.removeEventListener('scroll', handler)
        } else {
          section.removeEventListener('scroll', handler)
        }
      })
    }
    this._listeners = []
    const sections = [...new Set(this.elementContexts.map(ctx => ctx.section))]
    sections.forEach(section => {
      const handler = () => this.handleSimpleParallax()
      if (section === window) {
        window.addEventListener('scroll', handler)
      } else {
        section.addEventListener('scroll', handler)
      }
      this._listeners.push({ section, handler })
    })
    window.addEventListener('resize', () => this.handleSimpleParallax())
  }

  handleSimpleParallax() {
    this.elementContexts.forEach(({ element, section }) => {
      let scrollY = 0
      let parentRect
      if (section === window) {
        scrollY = window.scrollY
        parentRect = element.parentElement.getBoundingClientRect()
      } else {
        scrollY = section.scrollTop
        parentRect = element.parentElement.getBoundingClientRect()
      }
      const isVisible = parentRect.top < window.innerHeight && parentRect.bottom > 0
      if (isVisible) {
        const speed = parseFloat(element.dataset.simpleSpeed) || 0
        const xSpeed = parseFloat(element.dataset.simpleXSpeed) || 0
        const yPos = scrollY * speed
        const xPos = scrollY * xSpeed
        const parentWidth = element.parentElement.offsetWidth
        const parentHeight = element.parentElement.offsetHeight
        const elementWidth = element.offsetWidth
        const elementHeight = element.offsetHeight
        const maxYMovement = Math.max(0, (parentHeight - elementHeight) * 0.3)
        const maxXMovement = Math.max(0, (parentWidth - elementWidth) * 0.3)
        const clampedYPos = Math.max(-maxYMovement, Math.min(maxYMovement, yPos))
        const clampedXPos = Math.max(-maxXMovement, Math.min(maxXMovement, xPos))
        element.style.transform = `translate3d(${clampedXPos}px, ${clampedYPos}px, 0)`
      }
    })
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener('DOMContentLoaded', function () {
  // Initialize both parallax systems when the DOM is fully loaded
  new ParallaxAnimator()
  new ParallaxSimpleAnimator()
})